import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Tooltip,
  Box,
  Button,
  Popover,
  Chip,
} from "@mui/material";
import DeleteConfirmationModal from "../deleteConfirmationModal";
import CustomTypography from "../../globalStyles/fonts";
import InventoryConfigDetailsModal from "./inventoryConfigDetailsModal";
import { isMobile } from "../../globalStyles/mobile";
import { toast } from "react-toastify";
import Pagination from "../pagination";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "../../globalStyles/icons/editIcon";
import ViewIcon from "../../globalStyles/icons/vieewIcon";
import DeleteIcon from "../../globalStyles/icons/deleteIcon";
import { styled } from "@mui/material/styles";
import { tableCellClasses } from "@mui/material/TableCell";
import { inventoryConfigService } from "../../utils/services/inventory-config.service";

const InventoryConfigList = ({
  viewMode,
  data,
  onEditData,
  onDeleteData,
  setPage,
  setRowsPerPage,
  rowsPerPage,
  page,
  dataCount,
  selectedConfig,
  setSelectedConfig,
}) => {
  const [deletingItemId, setDeletingItemId] = useState(null);
  const [configData, setConfigData] = useState([]);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorPosition, setAnchorPosition] = useState({ top: 0, left: 0 });
  const [anchorPositionFilter, setAnchorPositionFilter] = useState(null);
  const startIndex = rowsPerPage * page;

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: "var(--table-header-bg)",
      border: "1px solid var(--border-color)",
      color: theme.palette.common.white,
      padding: "6px 6px",
      // textAlign: "center",
      // verticalAlign: "middle",
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 12,
      padding: "4px 4px",
      // textAlign: "center",
      // verticalAlign: "middle",
    },
  }));

  const StyledTableRow = styled(TableRow)(({}) => ({
    "&:nth-of-type(odd)": {
      backgroundColor: "var(--row-bg-odd)",
      border: "1px solid var(--border-color)",
    },
    "&:nth-of-type(even)": {
      backgroundColor: "var(--row-bg-even)",
      border: "1px solid var(--border-color)",
    },

    "&:lastChild td, &:lastChild th": {
      border: 0,
    },
  }));

  const deleteData = async () => {
    try {
      await inventoryConfigService.delete(deletingItemId);
      setOpenConfirmation(false);
      onDeleteData && onDeleteData();
      toast.success("Inventory config deleted successfully!");
    } catch (error) {
      toast.error("Failed to delete");
      console.error("Delete failed:", error);
    }
  };

  const handleViewClick = (config) => {
    setSelectedConfig(config);
    setOpenViewModal(true);
  };

  const handleDeleteClick = (item) => {
    setSelectedConfig(item);
    setDeletingItemId(item._id);
    setOpenConfirmation(true);
  };

  const handlePopoverOpen = (event, item) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setAnchorEl(event.currentTarget);
    setAnchorPositionFilter({
      top: rect.top,
      left: rect.left,
    });
    setSelectedConfig(item);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setAnchorPosition(null);
  };

  useEffect(() => {
    setConfigData(
      data?.map((item) => ({
        ...item,
        action: (
          <Box>
            <IconButton
              aria-label="more actions"
              onClick={(e) => handlePopoverOpen(e, item)}
              sx={{
                color:
                  anchorEl && selectedConfig?._id === item._id
                    ? "primary.main"
                    : "inherit",
              }}
            >
              <MoreVertIcon />
            </IconButton>
            <Popover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={handlePopoverClose}
              anchorOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              anchorReference="anchorPosition"
              anchorPosition={
                anchorPositionFilter
                  ? {
                      top: anchorPositionFilter.top,
                      left: anchorPositionFilter.left,
                    }
                  : undefined
              }
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              sx={{
                "& .MuiPaper-root": {
                  boxShadow: "none",
                  border: "1px solid #EEEEEE",
                  borderRadius: "4px",
                },
              }}
            >
              <Box
                display="flex"
                flexDirection="column"
                padding="8px"
                minWidth="150px"
              >
                <Button
                  style={{
                    color: "var(--text-tertiary)",
                    justifyContent: "flex-start",
                    textTransform: "none",
                  }}
                  aria-label="view"
                  onClick={() => {
                    handleViewClick(selectedConfig);
                    handlePopoverClose();
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-start"
                    width="100%"
                    gap={1}
                  >
                    <ViewIcon />
                    <Typography
                      fontFamily={"poppins"}
                      fontSize={"16px"}
                      color={"var(--text-tertiary)"}
                      fontWeight={500}
                    >
                      View
                    </Typography>
                  </Box>
                </Button>

                <Button
                  style={{
                    color: "var(--text-tertiary)",
                    justifyContent: "flex-start",
                    textTransform: "none",
                  }}
                  aria-label="edit"
                  onClick={() => {
                    onEditData && onEditData(selectedConfig);
                    handlePopoverClose();
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-start"
                    width="100%"
                    gap={1}
                  >
                    <EditIcon />
                    <Typography
                      fontFamily={"poppins"}
                      fontSize={"16px"}
                      color={"var(--text-tertiary)"}
                      fontWeight={500}
                    >
                      Edit
                    </Typography>
                  </Box>
                </Button>
                <Button
                  style={{
                    justifyContent: "flex-start",
                    textTransform: "none",
                  }}
                  aria-label="delete"
                  onClick={() => {
                    handleDeleteClick(selectedConfig);
                    handlePopoverClose();
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-start"
                    width="100%"
                    gap={1}
                  >
                    <DeleteIcon />
                    <Typography
                      fontFamily={"poppins"}
                      fontSize={"16px"}
                      fontWeight={500}
                      color={"var(--text-tertiary)"}
                    >
                      Delete
                    </Typography>
                  </Box>
                </Button>
              </Box>
            </Popover>
          </Box>
        ),
      })),
    );
  }, [data, anchorEl]);

  const renderGridView = () => (
    <Grid
      sx={{
        marginTop: "1px",
        maxHeight: "100%",
        paddingBottom: 5,
        overflow: "auto",
      }}
      container
      spacing={2}
    >
      {configData?.map((x, index) => (
        <Grid
          sx={{ paddingBottom: 1 }}
          item
          xs={12}
          sm={6}
          md={4}
          key={`${x._id} + ${index}`}
        >
          <Card
            sx={{
              boxShadow: 3,
              cursor: "pointer",
              width: "100%",
              height: "100%",
              position: "relative",
              borderRadius: "6px",
              border: "1px solid #D0D5DD",
            }}
          >
            <CardContent>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Typography
                  fontWeight="500"
                  sx={{ textAlign: "start" }}
                  fontFamily="Poppins"
                  color="var(--text-tertiary)"
                >
                  {startIndex + index + 1}
                </Typography>
                <div
                  style={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                  }}
                >
                  {x?.action}
                </div>
              </div>
              <Grid container spacing={1} mt={1}>
                {/* <Grid item xs={5} display="flex" alignItems="center">
                  <Typography
                    fontWeight="500"
                    sx={{ textAlign: "start" }}
                    fontFamily="Poppins"
                    color="var(--text-tertiary)"
                  >
                    Product
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Tooltip title={x?.product?.name} arrow>
                    <Typography
                      noWrap
                      sx={{ textAlign: "start" }}
                      fontWeight="500"
                      fontFamily={"poppins"}
                      color={"var(--text-tertiary)"}
                    >
                      : {x?.product?.name}
                    </Typography>
                  </Tooltip>
                </Grid> */}

                <Grid item xs={5} display="flex" alignItems="center">
                  <Typography
                    fontWeight="500"
                    sx={{ textAlign: "start" }}
                    fontFamily="Poppins"
                    color="var(--text-tertiary)"
                  >
                    SKU
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Tooltip title={x?.sku} arrow>
                    <Typography
                      noWrap
                      sx={{ textAlign: "start" }}
                      fontWeight="500"
                      fontFamily={"poppins"}
                      color={"var(--text-tertiary)"}
                    >
                      : {x?.sku}
                    </Typography>
                  </Tooltip>
                </Grid>

                <Grid item xs={5} display="flex" alignItems="center">
                  <Typography
                    fontWeight="500"
                    sx={{ textAlign: "start" }}
                    fontFamily="Poppins"
                    color="var(--text-tertiary)"
                  >
                    Supplier
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Tooltip title={x?.supplier?.name} arrow>
                    <Typography
                      noWrap
                      sx={{ textAlign: "start" }}
                      fontWeight="500"
                      fontFamily={"poppins"}
                      color={"var(--text-tertiary)"}
                    >
                      : {x?.supplier?.name}
                    </Typography>
                  </Tooltip>
                </Grid>

                <Grid item xs={5} display="flex" alignItems="center">
                  <Typography
                    fontWeight="500"
                    sx={{ textAlign: "start" }}
                    fontFamily="Poppins"
                    color="var(--text-tertiary)"
                  >
                    Warehouse
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Tooltip title={x?.warehouse?.name} arrow>
                    <Typography
                      noWrap
                      sx={{ textAlign: "start" }}
                      fontWeight="500"
                      fontFamily={"poppins"}
                      color={"var(--text-tertiary)"}
                    >
                      : {x?.warehouse?.name}
                    </Typography>
                  </Tooltip>
                </Grid>

                <Grid item xs={5} display="flex" alignItems="center">
                  <Typography
                    fontWeight="500"
                    sx={{ textAlign: "start" }}
                    fontFamily="Poppins"
                    color="var(--text-tertiary)"
                  >
                    Safety Stock
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography
                    noWrap
                    sx={{ textAlign: "start" }}
                    fontWeight="500"
                    fontFamily={"poppins"}
                    color={"var(--text-tertiary)"}
                  >
                    : {x?.safety_stock_count}
                  </Typography>
                </Grid>

                <Grid item xs={5} display="flex" alignItems="center">
                  <Typography
                    fontWeight="500"
                    sx={{ textAlign: "start" }}
                    fontFamily="Poppins"
                    color="var(--text-tertiary)"
                  >
                    Status
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Chip
                    label={x?.is_active ? "Active" : "Inactive"}
                    size="small"
                    sx={{
                      backgroundColor: x?.is_active
                        ? "var(--bg-highlight)"
                        : "#FFDEDF",
                      color: x?.is_active ? "#0070FF" : "#D13438",
                      fontWeight: 500,
                      fontSize: "10px",
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const NoDataImage = () => (
    <div
      style={{
        width: "100%",
        justifyContent: "center",
        display: "flex",
      }}
    >
      <img
        src="no_data_rateCard.png"
        alt="No data available"
        style={{ objectFit: "fill", height: "300px" }}
      />
    </div>
  );

  const renderListView = () => (
    <div
      style={{
        width: "100%",
        overflow: isMobile() ? "auto" : "hidden",
      }}
    >
      <TableContainer
        sx={{
          overflowY: "auto",
          maxHeight: "100%",
        }}
      >
        <Table
          stickyHeader
          aria-label="sticky table"
          sx={{
            border: "1px solid var(--border-color)",
          }}
        >
          <TableHead>
            <StyledTableRow>
              <StyledTableCell>
                <Typography
                  fontSize="14px"
                  color="var(--text-tertiary)"
                  fontWeight={500}
                  fontFamily={"poppins"}
                >
                  SL NO
                </Typography>
              </StyledTableCell>
              {/* <StyledTableCell>
                <Typography
                  fontSize="14px"
                  color="var(--text-tertiary)"
                  fontWeight={500}
                  fontFamily={"poppins"}
                >
                  Product
                </Typography>
              </StyledTableCell> */}
              <StyledTableCell>
                <Typography
                  fontSize="14px"
                  color="var(--text-tertiary)"
                  fontWeight={500}
                  fontFamily={"poppins"}
                >
                  SKU
                </Typography>
              </StyledTableCell>
              <StyledTableCell>
                <Typography
                  fontSize="14px"
                  color="var(--text-tertiary)"
                  fontWeight={500}
                  fontFamily={"poppins"}
                >
                  Supplier
                </Typography>
              </StyledTableCell>
              <StyledTableCell>
                <Typography
                  fontSize="14px"
                  color="var(--text-tertiary)"
                  fontWeight={500}
                  fontFamily={"poppins"}
                >
                  Warehouse
                </Typography>
              </StyledTableCell>
              <StyledTableCell>
                <Typography
                  fontSize="14px"
                  color="var(--text-tertiary)"
                  fontWeight={500}
                  fontFamily={"poppins"}
                >
                  Safety Stock
                </Typography>
              </StyledTableCell>
              <StyledTableCell>
                <Typography
                  fontSize="14px"
                  color="var(--text-tertiary)"
                  fontWeight={500}
                  fontFamily={"poppins"}
                >
                  Min Order Qty
                </Typography>
              </StyledTableCell>
              <StyledTableCell>
                <Typography
                  fontSize="14px"
                  color="var(--text-tertiary)"
                  fontWeight={500}
                  fontFamily={"poppins"}
                >
                  Avg Fulfillment
                </Typography>
              </StyledTableCell>
              <StyledTableCell>
                <Typography
                  fontSize="14px"
                  color="var(--text-tertiary)"
                  fontWeight={500}
                  fontFamily={"poppins"}
                >
                  Status
                </Typography>
              </StyledTableCell>
              <StyledTableCell>
                <CustomTypography
                  fontSize="14px"
                  title={true}
                  color="var(--text-tertiary)"
                  fontWeight={500}
                ></CustomTypography>
              </StyledTableCell>
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {configData?.map((x, index) => (
              <React.Fragment key={`${x._id} + ${index}`}>
                <StyledTableRow key={index}>
                  <StyledTableCell>
                    <Typography
                      fontSize="12px"
                      color="#667085"
                      fontWeight={400}
                      fontFamily={"Poppins"}
                    >
                      {startIndex + index + 1}
                    </Typography>
                  </StyledTableCell>
                  {/* <StyledTableCell>
                    <Typography
                      fontSize="12px"
                      color="#667085"
                      fontWeight={400}
                      fontFamily={"Poppins"}
                      maxWidth={250}
                    >
                      {x?.product?.name}
                    </Typography>
                  </StyledTableCell> */}
                  <StyledTableCell>
                    <Typography
                      fontSize="12px"
                      color="#667085"
                      fontWeight={400}
                      fontFamily={"Poppins"}
                    >
                      {x?.sku}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography
                      fontSize="12px"
                      color="#667085"
                      fontWeight={400}
                      fontFamily={"Poppins"}
                    >
                      {x?.supplier?.name}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography
                      fontSize="12px"
                      color="#667085"
                      fontWeight={400}
                      fontFamily={"Poppins"}
                    >
                      {x?.warehouse?.name}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography
                      fontSize="12px"
                      color="#667085"
                      fontWeight={400}
                      fontFamily={"Poppins"}
                    >
                      {x?.safety_stock_count}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography
                      fontSize="12px"
                      color="#667085"
                      fontWeight={400}
                      fontFamily={"Poppins"}
                    >
                      {x?.min_order_quantity}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography
                      fontSize="12px"
                      color="#667085"
                      fontWeight={400}
                      fontFamily={"Poppins"}
                    >
                      {x?.avg_fullfillment_days} days
                    </Typography>
                    <Typography
                      fontSize="12px"
                      color="#667085"
                      fontWeight={400}
                      fontFamily={"Poppins"}
                    >
                      {`${
                        x?.avg_fullfillment_days_supplier || 0
                      } days (Supplier)`}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Chip
                      label={x?.is_active ? "Active" : "Inactive"}
                      size="small"
                      sx={{
                        backgroundColor: x?.is_active
                          ? "var(--bg-highlight)"
                          : "#FFDEDF",
                        color: x?.is_active ? "#0070FF" : "#D13438",
                        fontWeight: 500,
                        fontSize: "10px",
                      }}
                    />
                  </StyledTableCell>
                  <StyledTableCell sx={{ width: "3%", textAlign: "center" }}>
                    {x?.action}
                  </StyledTableCell>
                </StyledTableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );

  return (
    <div sx={{ minWidth: "100%" }}>
      <div
        style={{
          overflowY: "scroll",
          display: "flex",
          maxHeight: isMobile() ? "60vh" : `calc(100vh - 260px)`,
          width: "100%",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::WebkitScrollbar": { width: 0 },
        }}
      >
        {viewMode === "grid" || isMobile()
          ? renderGridView()
          : renderListView()}
      </div>
      <Pagination
        dataCount={dataCount}
        rowsPerPage={rowsPerPage}
        page={page}
        setPage={setPage}
        setRowsPerPage={setRowsPerPage}
        disableNext={dataCount <= rowsPerPage * (page + 1)}
      />
      <DeleteConfirmationModal
        title={`Are you sure you want to delete this inventory configuration?`}
        open={openConfirmation}
        handleClose={() => setOpenConfirmation(false)}
        handleDelete={() => {
          deleteData();
        }}
      />
      <InventoryConfigDetailsModal
        config={selectedConfig}
        open={openViewModal}
        onClose={() => setOpenViewModal(false)}
      />
    </div>
  );
};

export default InventoryConfigList;
